import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import CardsImage from '../../../../../common/assets/images/payments-card.svg';
import EdenredCardImage from '../../../../../common/assets/images/payments-edenred.svg';
import { formatPrice } from '../../../../../common/global/helpers';
import Icons from '../../../../../common/widgets/Icons';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import { config } from '../../../config';
import { paymentMethodCodes } from '../../../constants';
import { CustomerBudgetContext } from '../../../contexts/CustomerBudgetContext';
import { CartSummaryModel } from '../../../models/Cart';
import { resources } from '../../../resources/resources';
import { cashClubMethodCode, freeCheckoutMethodCode } from '../CartPageCommon';

interface CartPaymentMethodsProps {
  summary: CartSummaryModel;
  isSummaryLoading: boolean;
  selectedCheckoutMethod: string | undefined;
  setSelectedCheckoutMethod: (checkoutCode: string) => void;
}

export const CartPaymentMethods = (props: CartPaymentMethodsProps) => {
  const {
    summary,
    isSummaryLoading,
    selectedCheckoutMethod,
    setSelectedCheckoutMethod,
  } = props;
  const { customerBudget } = useContext(CustomerBudgetContext);
  const { t: translate } = useTranslation();

  const paymentMethodsUi = (
    paymentMethod: string,
  ): { title: string; description: string; image: string } => {
    switch (paymentMethod) {
      case paymentMethodCodes.mobilpay:
        return {
          title: translate(resources.cart.paymentOptions.cardTitle),
          description: '',
          image: CardsImage,
        };
      case paymentMethodCodes.mobilpaycv:
        return {
          title: translate(resources.cart.paymentOptions.edenredCvTitle),
          description: translate(
            resources.cart.paymentOptions.edenredCvDescription,
          ),
          image: EdenredCardImage,
        };
      case paymentMethodCodes.mobilpaygv:
        return {
          title: translate(resources.cart.paymentOptions.edenredGvTitle),
          description: translate(
            resources.cart.paymentOptions.edenredGvDescription,
          ),
          image: EdenredCardImage,
        };
      case paymentMethodCodes.mobilpaytv:
        return {
          title: translate(resources.cart.paymentOptions.edenredTvTitle),
          description: translate(
            resources.cart.paymentOptions.edenredTvDescription,
          ),
          image: EdenredCardImage,
        };
      case paymentMethodCodes.cashclub:
        return {
          title: translate(resources.cart.paymentOptions.cashClubTitle),
          description: `${translate(
            resources.cart.paymentOptions.cashClubDescription,
          )}: ${formatPrice(customerBudget?.benefitBucksBalance)} ${
            config.currency
          }`,
          image: '',
        };
      case paymentMethodCodes.salaryDeduction:
        return {
          title: translate(resources.cart.paymentOptions.salaryDeductionTitle),
          description: translate(
            resources.cart.paymentOptions.salaryDeductionDescription,
          ),
          image: '',
        };
      default:
        throw new Error('Payment method not supported');
    }
  };

  const isCashClubEnabled =
    summary.cashbackDiscount > 0 &&
    summary.paidFromContribution == 0 &&
    summary.paidFromBudget == 0;

  const hasProductThatCanUseBenefitBudget = summary.products.some(
    p => p.isBenefitInKind && p.canUseBenefitBudget,
  );
  const hasBenefitPaymentMethod =
    (summary.products.some(p => p.paidFromBudget > 0) ||
      hasProductThatCanUseBenefitBudget) &&
    !isCashClubEnabled;
  const availablePaymentMethods = summary.paymentMethods.filter(
    p =>
      (p.code !== freeCheckoutMethodCode &&
        p.code !== cashClubMethodCode &&
        !isCashClubEnabled) ||
      (p.code === cashClubMethodCode && isCashClubEnabled),
  );
  const hasCheckoutPaymentMethods =
    (summary.paidFromContribution > 0 || isCashClubEnabled) &&
    availablePaymentMethods.length > 0;

  if (
    !isSummaryLoading &&
    !hasBenefitPaymentMethod &&
    !hasCheckoutPaymentMethods
  ) {
    return null;
  }

  return (
    <>
      <div>
        <h2>{translate(resources.cart.title.payment)}</h2>
      </div>
      <div className="checkout-payments">
        <Loader loading={isSummaryLoading} loader={<LoadingSpinner />}>
          <ul className="checkout-payments-options list-reset">
            {hasBenefitPaymentMethod && (
              <li>
                <div
                  className={`payment-method-box benefits-box ${
                    summary.paidFromBudget > 0 ||
                    (summary.paidFromContribution === 0 &&
                      !summary.cashbackDiscount &&
                      hasProductThatCanUseBenefitBudget)
                      ? 'selected'
                      : ''
                  }`}
                >
                  <span className="payment-icon">
                    <Icons.Wallet />
                  </span>
                  <div className="payment-content">
                    <div className="payment-title">
                      {translate(resources.cart.paymentOptions.benefitsTitle)}{' '}
                      {formatPrice(customerBudget?.benefitBudgetBalance)}{' '}
                      {config.currency}
                    </div>
                  </div>
                </div>
              </li>
            )}

            {hasCheckoutPaymentMethods &&
              availablePaymentMethods.map(p => {
                const isSelected =
                  selectedCheckoutMethod === p.code || isCashClubEnabled;
                return (
                  <li key={p.code}>
                    <div
                      className={`payment-method-box ${
                        isSelected ? 'selected' : ''
                      }`}
                    >
                      <label className="k-radio-label">
                        <input
                          type="radio"
                          className="k-radio k-radio-md"
                          value=""
                          checked={isSelected}
                          onChange={() => setSelectedCheckoutMethod(p.code)}
                        />
                        <div className="payment-content">
                          <div className="payment-title">
                            {paymentMethodsUi(p.code).title}
                            {paymentMethodsUi(p.code).image !== '' && (
                              <span className="payment-image">
                                <img
                                  src={paymentMethodsUi(p.code).image}
                                  alt=""
                                />
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    </div>
                  </li>
                );
              })}
          </ul>
        </Loader>
      </div>
    </>
  );
};
